import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomTextInput from '../../../components/CustomTextInput';
import CustomSelectInput from '../../../components/CustomSelectInput';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { fetchBusinessProfilesRequest } from '../../../api/businessProfiles/businessProfilesSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../utils/axios';
import { useNavigate } from 'react-router-dom';
import { fetchOrgRequest } from '../../../api/organizations/orgSlice';

const MembersEmailSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
});

const TeamFormPopup = ({ open, handleClose, member, mode }) => {
  const dispatch = useDispatch();
  const org = useSelector((state) => state.org.org);
  const [formLoading, setFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState();
  const navigate = useNavigate();

  const { businessProfiles, loading } = useSelector((state) => state.businessProfiles);

  const modifySearchTerm = useCallback(
    debounce((value) => setSearchTerm(value), 500),
    [],
  );

  useEffect(() => {
    dispatch(
      fetchBusinessProfilesRequest({
        query: {
          page: 1,
          limit: 10,
          name: searchTerm || undefined,
        },
      }),
    );
  }, [searchTerm, dispatch]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(MembersEmailSchema),
    defaultValues: {
      permissions: {
        allowed_business_profiles: member?.permissions?.allowed_business_profiles || [],
        business_profiles: member?.permissions?.business_profiles || 1,
        customers: member?.permissions?.customers || 2,
        suppliers: member?.permissions?.suppliers || 2,
        documents: member?.permissions?.documents || 2,
        products: member?.permissions?.products || 1,
      },
      email: member?.email || '',
      name: member?.name || '',
    },
  });

  const accessOptions = [
    { value: 1, label: 'Read Only' },
    { value: 2, label: 'Read & Write' },
  ];

  const onContinue = async (data) => {
    setFormLoading(true);

    const request =
      mode === 'edit'
        ? apiClient.patch(`/organizations/${org?._id}/users/${member?._id}`, {
            ...data,
            permissions: {
              ...data.permissions,
              allowed_business_profiles: Array.isArray(data.permissions.allowed_business_profiles)
                ? typeof data.permissions.allowed_business_profiles[0] === 'string'
                  ? data.permissions.allowed_business_profiles
                  : data.permissions.allowed_business_profiles.map((p) => p.profile_id)
                : [],
            },
          })
        : apiClient.post(`/organizations/${org?._id}/users`, data);

    request
      .then((res) => {
        dispatch(showSnackbar({ message: res.data.message, severity: 'success' }));
        handleClose();
        reset();
        navigate('/settings?tab=member');
        dispatch(fetchOrgRequest());
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message || 'Something went wrong!',
            severity: 'warning',
          }),
        );
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          padding: 16,
          boxShadow: 24,
          outline: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Member</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider />
        <Stack sx={{ m: '1rem' }} spacing={2}>
          <CustomTextInput name="name" label="Name" control={control} errors={errors} />
          <CustomTextInput name="email" label="Email" control={control} errors={errors} />
          <CustomSelectInput
            name="permissions.business_profiles"
            control={control}
            errors={errors}
            options={accessOptions}
            label="Business Profile Management"
          />
          <CustomSelectInput
            name="permissions.customers"
            control={control}
            errors={errors}
            options={accessOptions}
            label="Customer Management"
          />
          <CustomSelectInput
            name="permissions.suppliers"
            control={control}
            errors={errors}
            options={accessOptions}
            label="Supplier Management"
          />
          <CustomSelectInput
            name="permissions.documents"
            control={control}
            errors={errors}
            options={accessOptions}
            label="Document Creation"
          />
          <CustomSelectInput
            name="permissions.products"
            control={control}
            errors={errors}
            options={accessOptions}
            label="Product Creation"
          />
          <Controller
            name="permissions.allowed_business_profiles"
            control={control}
            render={({ field }) => {
              const initialValue = Array.isArray(field.value)
                ? field.value.map((item) => (typeof item === 'object' ? item.profile_id : item))
                : [];
              const selectedProfiles = businessProfiles.filter((bp) => initialValue.includes(bp.profile_id));
              return (
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Autocomplete
                    multiple
                    loading={loading}
                    fullWidth
                    options={businessProfiles}
                    getOptionLabel={(option) =>
                      `${option?.name || ''} | ${option?.email || ''} | ${option?.registration_number || ''}`
                    }
                    getOptionKey={(option) => option.profile_id}
                    isOptionEqualToValue={(option, value) => option.profile_id === value.profile_id}
                    value={selectedProfiles}
                    onChange={(_, value) => {
                      field.onChange(value.map((v) => v.profile_id));
                    }}
                    onInputChange={(event, value, reason) => {
                      if (reason === 'input') {
                        modifySearchTerm(value);
                      }
                    }}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed Business Profiles"
                        error={!!errors.permissions?.allowed_business_profiles}
                        helperText={errors.permissions?.allowed_business_profiles?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              );
            }}
          />

          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onContinue)}>
              {formLoading ? 'Processing...' : 'Continue'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Modal>
  );
};

export default TeamFormPopup;
